/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      if (!this.variant
        && this.selectedOptions.every((o) => o !== null)
        && this.data.product.variants.length > 1) {
        const changedIndex = this.getChangedOptionIndex(evt);
        if (changedIndex !== -1) {
          this.clearUnavailableOptions(changedIndex);
          this.selectedOptions = this.getSelectedOptions();
          this.variant = this.getSelectedVariant();
        }
      }

      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      } else {
        this.clearVariantInput(evt);
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.updateEnquireButton();

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;

      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the "Enquire now" button disabled state.
     * Unlike add-to-cart, this enables once all options are selected
     * regardless of whether they match an existing variant.
     */
    updateEnquireButton() {
      this.enquireBtn = this.enquireBtn
        || this.productForm.querySelector('[data-enquire-now-button]');
      if (!this.enquireBtn) return;

      const allSelected = this.selectedOptions.every((o) => o !== null);

      this.enquireBtn.dataset.optionsIncomplete = (!allSelected).toString();
      this.enquireBtn.disabled = !allSelected;
      this.enquireBtn.textContent = allSelected
        ? this.enquireBtn.dataset.enquireText
        : theme.strings.noSelectedVariant;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update price.
        if (variant.compare_at_price > variant.price) {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.salePrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
            priceWasEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
          }
        } else {
          priceCurrentEl.querySelector('.js-label').textContent = theme.strings.regularPrice;
          priceCurrentEl.querySelector('.js-value').innerHTML = this.data.formatted[variant.id].price;
          if (priceWasEl) {
            priceWasEl.querySelector('.js-label').textContent = '';
            priceWasEl.querySelector('.js-value').innerHTML = '';
          }
        }

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }

      this.syncStickyPrice();
    }

    /**
     * Mirrors the main price block into the sticky add-to-cart panel.
     *
     * The panel renders its own copy of the `price` snippet and nothing kept it up to date. It is
     * rendered without `use_variant`, so `price.liquid` falls back to
     * `product.variants | sort: 'price' | first` — the CHEAPEST variant — and it never changed as
     * the customer picked options, because `updatePrice()` only ever queried
     * `.product-info__price > .price`. On a product whose variants differ in price the sticky bar
     * therefore quoted a price the customer could not actually buy.
     *
     * Copying the already-updated markup keeps a single source of truth rather than repeating the
     * surgery above. Scoped from `.cc-main-product`, not `.js-product`: the panel is a sibling of
     * the product block, not a descendant of it.
     */
    syncStickyPrice() {
      if (this.stickyPrice === undefined) {
        const wrapper = this.section.closest('.cc-main-product');
        this.stickyPrice = wrapper ? wrapper.querySelector('.sticky-atc-panel .price') : null;
      }
      if (!this.stickyPrice || !this.price) return;

      this.stickyPrice.className = this.price.className;
      this.stickyPrice.innerHTML = this.price.innerHTML;

      // Belt and braces: nothing payment-provider-shaped may live in the sticky panel. PayPal's
      // `payment_terms` output currently sits in `.product-info__block--price`, OUTSIDE the
      // `.price` element copied above, so it cannot arrive this way today — but the panel is a
      // strip beside a button with no room for instalment messaging, and provider scripts inject
      // themselves into arbitrary containers. Strip it rather than rely on that staying true.
      this.stickyPrice
        .querySelectorAll('shopify-payment-terms, .js-instalments-form, iframe')
        .forEach((node) => node.remove());
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Clears the hidden [name="id"] inputs and the ?variant= URL parameter.
     *
     * Now that an unmatched combination clears the offending options instead of snapping to
     * another variant, the page can sit in a genuine "no variant" state. Leaving the previous
     * variant's id behind is wrong in three ways: `enquire-form-modal-DG.js` reads that input and
     * would report a variant the customer is no longer looking at, `.js-instalments-form` would
     * quote instalments for it, and a refresh or shared link would restore options that are not
     * the ones on screen.
     * @param {object} evt - Event object.
     */
    clearVariantInput(evt) {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input || input.value === '') return;
        input.value = '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', this.dataset.url);
    }

    /**
     * Gets the index of the option selector that triggered the change event.
     * @param {object} evt - Event object.
     * @returns {number} Index of the changed selector, or -1 if not found.
     */
    getChangedOptionIndex(evt) {
      const changedSelector = evt.target.closest('.option-selector');
      if (!changedSelector) return -1;
      return Array.from(this.optionSelectors).indexOf(changedSelector);
    }

    /**
     * Clears later option choices that no longer lead to a variant that exists.
     *
     * When the customer changes an earlier option (colour), a later choice they already made
     * (size M) may not exist in the new combination. The theme used to snap them to the closest
     * variant, which silently changed their size from M to S — shipping a product they did not
     * choose. Those options are cleared instead, so the picker asks again.
     *
     * A later choice that still leads to a real variant is KEPT, including a sold-out one, so the
     * "Sold Out Online. Check in Store" message survives rather than being cleared away.
     * Options at or before the changed one are never touched — they are what the customer just
     * asked for.
     * @param {number} changedIndex - Index of the option that was changed.
     */
    clearUnavailableOptions(changedIndex) {
      const constraints = [];
      for (let i = 0; i <= changedIndex; i += 1) {
        if (this.selectedOptions[i] !== null) constraints.push(i);
      }

      for (let i = changedIndex + 1; i < this.selectedOptions.length; i += 1) {
        if (this.selectedOptions[i] === null) continue;

        const stillExists = this.data.product.variants.some(
          (v) => constraints.every((c) => v.options[c] === this.selectedOptions[c])
            && v.options[i] === this.selectedOptions[i]
        );

        if (stillExists) {
          constraints.push(i);
        } else {
          this.clearSelector(this.optionSelectors[i]);
          this.selectedOptions[i] = null;
        }
      }
    }

    /**
     * Resets a single option selector back to its unselected state.
     * Sets state directly rather than calling CustomSelect.selectOption(), which dispatches a
     * 'change' event and would re-enter handleVariantChange mid-update.
     * @param {Element} selector - The .option-selector element to clear.
     */
    clearSelector(selector) {
      if (selector.dataset.selectorType === 'dropdown') {
        const customSelect = selector.querySelector('custom-select');
        if (!customSelect) return;

        // Rendered only when nothing was pre-selected server-side. Without it there is no
        // unselected state to return to, so leave the selector alone.
        const placeholder = customSelect.querySelector('.custom-select__option[data-value=""]');
        if (!placeholder) return;

        if (customSelect.selectedOption) {
          customSelect.selectedOption.setAttribute('aria-selected', 'false');
        }
        placeholder.setAttribute('aria-selected', 'true');
        customSelect.selectedOption = placeholder;
        customSelect.button.firstElementChild.textContent = placeholder.firstElementChild
          ? placeholder.firstElementChild.textContent
          : placeholder.textContent.trim();
        customSelect.listbox.setAttribute('aria-activedescendant', placeholder.id);

        if (customSelect.swatches) {
          customSelect.button.dataset.swatch = '';
        }
        if (customSelect.nativeSelect) {
          customSelect.nativeSelect.value = '';
        }
      } else {
        selector.querySelectorAll('input.js-option:checked').forEach((input) => {
          input.checked = false;
        });
        const colorText = selector.querySelector('.js-color-text');
        if (colorText) colorText.textContent = '';
      }
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => selectedOptions.every((val, index) => val === v.options[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
